import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useState } from "react";
import { AlertCircle, ArrowRight } from "lucide-react";

import Field from "./ui/Field";
import ConsentCheckbox from "./ui/ConsentCheckbox";

// Import Firestore functions and the database instance
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  serverTimestamp 
} from "firebase/firestore";
import { db } from "../firebase/firebase";

import styles from "../styles/Masterclass.module.scss";
import { CONTENT } from "../constants/content";

import heroImage from "../assets/images/caterina-about2.jpg";
import mc480 from "../assets/images/caterina-about2-480.webp";
import mc768 from "../assets/images/caterina-about2-768.webp";
import mc1200 from "../assets/images/caterina-about2-1200.webp";

const MC_SRCSET = [`${mc480} 480w`, `${mc768} 768w`, `${mc1200} 1200w`].join(", ");

const Masterclass = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const masterclassContent = CONTENT.masterclass;
  const masterclassForm = masterclassContent.form;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    languageLevel: "",
    reason: "",
    gdprConsent: false,
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const normalizeEmail = (email) => email.trim().toLowerCase();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate required fields
    if (!formData.name || !formData.email) {
      setError("Per favore, compila tutti i campi obbligatori.");
      return;
    }

    if (!formData.gdprConsent) {
      setError("Per favore, accetta i termini di consenso GDPR.");
      return;
    }

    try {
      const emailKey = normalizeEmail(formData.email);
      const masterclassRef = doc(collection(db, "masterclass"), emailKey);

      // Check if the document already exists
      const docSnapshot = await getDoc(masterclassRef);
      if (docSnapshot.exists()) {
        setError("Questa email risulta già registrata per la masterclass.");
        return;
      }

      // Create the document with deterministic ID to enforce uniqueness
      await setDoc(masterclassRef, {
        name: formData.name,
        email: formData.email,
        emailKey,
        phone: formData.phone || null,
        languageLevel: formData.languageLevel || null,
        reason: formData.reason || null,
        gdprConsentAccepted: formData.gdprConsent,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error("Error adding document: ", err);
      setError("C'è stato un errore. Riprova più tardi.");
    }
  };

  const animationVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (delay = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, delay },
    }),
  };

  return (
    <div className={styles.masterclass} ref={ref}>
      <header className={styles.heroSection}>
        <div className={styles.textColumn}>
          <motion.span
            className={styles.badge}
            variants={animationVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            custom={0}
          >
            {masterclassContent.badge}
          </motion.span>

          <motion.h1
            className={styles.title}
            variants={animationVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            custom={0.1}
          >
            {masterclassContent.title}
          </motion.h1>

          <motion.p
            className={styles.subtitle}
            variants={animationVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            custom={0.2}
          >
            {masterclassContent.subtitle}
          </motion.p>

          <motion.ul
            className={styles.detailsList}
            variants={animationVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            custom={0.3}
          >
            {masterclassContent.details.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          className={styles.imageColumn}
          variants={animationVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          custom={0.4}
        >
          <img
            src={heroImage}
            srcSet={MC_SRCSET}
            sizes="(min-width: 993px) 48vw, 100vw"
            width="1800"
            height="1200"
            alt="Incontro Masterclass ALPS"
            loading="lazy"
            decoding="async"
          />
        </motion.div>
      </header>

      <section className={styles.formSection} aria-label="Iscrizione alla masterclass">
        <motion.div
          className={styles.formCard}
          variants={animationVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          custom={0.5}
        >
          {isSubmitted ? (
            <p className={styles.successMessage}>
              {masterclassForm.successMessage}
            </p>
          ) : (
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formHeader}>
                <h2 className={styles.formTitle}>
                  {masterclassForm.title}
                </h2>
                <p className={styles.formSubtitle}>
                  {masterclassForm.subtitle}
                </p>
              </div>

              <Field
                id="name"
                type="text"
                label={masterclassForm.fields.name.label}
                placeholder={masterclassForm.fields.name.placeholder}
                value={formData.name}
                onChange={handleChange}
                autoComplete="name"
                required
              />

              <Field
                id="email"
                type="email"
                label={masterclassForm.fields.email.label}
                placeholder={masterclassForm.fields.email.placeholder}
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />

              <Field
                id="phone"
                type="tel"
                label={masterclassForm.fields.phone.label}
                placeholder={masterclassForm.fields.phone.placeholder}
                value={formData.phone}
                onChange={handleChange}
                autoComplete="tel"
              />

              <Field
                as="select"
                id="languageLevel"
                label={masterclassForm.fields.languageLevel.label}
                value={formData.languageLevel}
                onChange={handleChange}
              >
                <option value="">{masterclassForm.fields.languageLevel.placeholder}</option>
                <option value="A1">A1 - Principiante</option>
                <option value="A2">A2 - Elementare</option>
                <option value="B1">B1 - Intermedio</option>
                <option value="B2">B2 - Intermedio avanzato</option>
                <option value="C1">C1 - Avanzato</option>
              </Field>

              <Field
                as="textarea"
                id="reason"
                label={masterclassForm.fields.reason.label}
                placeholder={masterclassForm.fields.reason.placeholder}
                value={formData.reason}
                onChange={handleChange}
                rows="4"
              />

              <ConsentCheckbox
                checked={formData.gdprConsent}
                onChange={handleChange}
              >
                {masterclassForm.gdprConsentText}
              </ConsentCheckbox>

              <button type="submit" className={styles.submitButton}>
                {masterclassForm.buttonText}
                <ArrowRight className={styles.buttonIcon} aria-hidden="true" />
              </button>

              {error && (
                <p className={styles.errorMessage} role="alert">
                  <AlertCircle aria-hidden="true" />
                  {error}
                </p>
              )}
            </form>
          )}
        </motion.div>
      </section>
    </div>
  );
};

export default Masterclass;


